/**
 * STOY: country and language picker (snippets/site-picker.liquid).
 *
 * A dropdown on the button: it opens above it (below in the header, or when there's no room above) and closes on a
 * click elsewhere, Escape or when focus leaves it. On phones the same <dialog> opens modal instead, as a sheet over the
 * bottom half of the screen (see the CSS) that closes on a tap on the dimmed page above it; the browser keeps focus in
 * it, closes it on Escape and gives focus back to the button.
 * Each site row is a submit button of Shopify's localization form carrying its country; its language is copied into
 * the form here. Both forms come back to the page being viewed, in the chosen site.
 */
if (!customElements.get('site-picker')) {
  class SitePicker extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('.site-picker--toggle');
      this.panel = this.querySelector('.site-picker--panel');
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      this.onKeydown = (event) => {
        if (event.key !== 'Escape') return;
        this.close();
        this.toggle.focus();
      };

      this.toggle.addEventListener('click', () => (this.panel.open ? this.close() : this.open()));
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });
      this.panel.addEventListener('click', (event) => {
        // The dialog itself is only the target for taps on the dimmed page around the phone sheet.
        if (event.target === this.panel || event.target.closest('[data-site-picker-close]')) this.close();
      });
      // However it closes, Escape in the phone sheet included.
      this.panel.addEventListener('close', () => {
        this.toggle.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
      });

      this.panel.addEventListener('submit', (event) => {
        const language = event.submitter && event.submitter.dataset.language;
        if (language) event.target.querySelector('input[name="language_code"]').value = language;
        // The URL can have changed since the page loaded (variant picked, filters set).
        const returnTo = event.target.querySelector('input[name="return_to"]');
        if (returnTo) returnTo.value = window.location.pathname + window.location.search;
      });
      this.panel.querySelector('.site-picker--other-select').addEventListener('change', (event) => {
        event.target.form.requestSubmit();
      });
    }

    disconnectedCallback() {
      this.close();
    }

    open() {
      this.toggle.setAttribute('aria-expanded', 'true');
      if (window.matchMedia('(max-width: 767px)').matches) {
        this.panel.showModal();
        return;
      }
      this.panel.show();
      // Above the button unless there's more room below it (the header opens below by itself, see the CSS).
      const button = this.toggle.getBoundingClientRect();
      this.classList.toggle('site-picker--below', button.top < this.panel.offsetHeight + 16 && window.innerHeight - button.bottom > button.top);
      // On the current site, not on the list's scroll area, which show() picks.
      this.panel.querySelector('[aria-current]').focus();
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      if (this.panel && this.panel.open) this.panel.close();
    }
  }
  customElements.define('site-picker', SitePicker);
}
